/**
 * Renders the stable status vocabulary used by the OCI review dashboard.
 *
 * Central indicators make clear, flagged, stale, not-run, and failed reviews
 * visually distinct without coupling business-state derivation to badge/card
 * presentation.
 */
import { AlertTriangle, CheckCircle2, Loader2, ShieldAlert } from 'lucide-react'

import { Badge, Card, CardContent } from '@/components/ui'

import type { OciFeedActivity, OciReviewStatus } from './ociDashboardModel'


export function OciActivityBadge({ activity }: { activity: OciFeedActivity }) {
  return (
    <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100" role="status">
      <Loader2 className="mr-1 h-3 w-3 animate-spin" />
      {activity === 'syncing' ? 'Syncing…' : 'Analyzing…'}
    </Badge>
  )
}

export function OciStatusBadge({
  status,
  count,
  documentName,
  activity,
  onOpen,
}: {
  status: OciReviewStatus
  count: number
  documentName: string
  activity?: OciFeedActivity
  onOpen: () => void
}) {
  if (activity && (status === 'not-run' || status === 'stale')) {
    return <OciActivityBadge activity={activity} />
  }
  if (status === 'flagged') {
    return (
      <Badge asChild variant="destructive" className="cursor-pointer hover:bg-destructive/90">
        <button
          type="button"
          aria-label={`View ${count} OCI finding${count === 1 ? '' : 's'} for ${documentName}`}
          onClick={onOpen}
        >
          {count} OCI finding{count === 1 ? '' : 's'}
        </button>
      </Badge>
    )
  }
  if (status === 'clear') {
    return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Clear</Badge>
  }
  if (status === 'stale') {
    return <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100">Needs rerun</Badge>
  }
  if (status === 'error') return <Badge variant="destructive">Run failed</Badge>
  return <Badge variant="secondary">Not analyzed</Badge>
}

export function Metric({
  label,
  value,
  tone,
  testId,
}: {
  label: string
  value: number
  tone?: 'success' | 'danger' | 'warning'
  testId?: string
}) {
  const Icon = tone === 'danger'
    ? ShieldAlert
    : tone === 'warning'
      ? AlertTriangle
      : CheckCircle2
  const iconClass = tone === 'danger'
    ? 'bg-red-50 text-red-500'
    : tone === 'warning'
      ? 'bg-amber-50 text-amber-600'
      : 'bg-emerald-50 text-emerald-600'
  return (
    <Card className="gap-0 py-0" data-testid={testId}>
      <CardContent className="flex items-center gap-3 p-3">
        <div className={`flex size-9 shrink-0 items-center justify-center rounded-md ${iconClass}`}>
          <Icon className="size-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-xl font-semibold tabular-nums">{value}</p>
        </div>
      </CardContent>
    </Card>
  )
}
